import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, BarChart2, MessageSquare, Users, AlertTriangle,
  HelpCircle, Clock, Shield, X, Check, Ban, Volume2, VolumeX,
  Trash2, Flag, ChevronRight, AlertCircle, Image, TrendingUp
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../contexts/AuthContext";
import AvatarDisplay from "../components/AvatarDisplay";

const STATUS_COLORS = {
  pending:      "bg-amber-500/20 text-amber-400 border-amber-500/30",
  under_review: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  action_taken: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  dismissed:    "bg-slate-700/50 text-slate-500 border-slate-700",
  closed:       "bg-slate-700/50 text-slate-500 border-slate-700",
  open:         "bg-amber-500/20 text-amber-400 border-amber-500/30",
  in_progress:  "bg-blue-500/20 text-blue-400 border-blue-500/30",
  resolved:     "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
};

// ── Action modal ──────────────────────────────────────────────────────────────
function ActionModal({ report, onClose, onDone }) {
  const [actionType, setActionType] = useState("");
  const [durationHours, setDurationHours] = useState(24);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ACTIONS = [
    { value: "dismiss",        label: "Dismiss",        icon: <X className="w-4 h-4" />,        desc: "No action required",            color: "text-slate-400" },
    { value: "warn",           label: "Warn User",      icon: <AlertTriangle className="w-4 h-4" />, desc: "Issue a formal warning",    color: "text-amber-400" },
    { value: "remove_message", label: "Remove Message", icon: <Trash2 className="w-4 h-4" />,    desc: "Delete the reported message",   color: "text-orange-400", needsMsg: true },
    { value: "mute",           label: "Mute User",      icon: <VolumeX className="w-4 h-4" />,  desc: "Prevent messaging",             color: "text-rose-400",   hasDuration: true },
    { value: "suspend",        label: "Suspend",        icon: <Clock className="w-4 h-4" />,    desc: "Temporary account suspension",  color: "text-red-400",    hasDuration: true },
    { value: "ban",            label: "Permanent Ban",  icon: <Ban className="w-4 h-4" />,      desc: "Permanently remove access",     color: "text-red-600" },
  ];

  const DURATIONS = [
    { value: 0.167, label: "10 minutes" },
    { value: 1,     label: "1 hour" },
    { value: 24,    label: "24 hours" },
    { value: 168,   label: "7 days" },
    { value: 720,   label: "30 days" },
  ];

  const selected = ACTIONS.find(a => a.value === actionType);

  const handleSubmit = async () => {
    if (!actionType || !reason.trim()) { setError("Select an action and provide a reason."); return; }
    if (selected?.needsMsg && !report.msg_id) { setError("This action requires a message report."); return; }
    setLoading(true);
    try {
      await api.post(`/admin/reports/${report.report_id}/action`, {
        action_type: actionType,
        reason: reason.trim(),
        duration_hours: selected?.hasDuration ? durationHours : null
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to take action");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0E0E17] border border-purple-500/25 rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-purple-500/15 shrink-0">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-400" />
            <span className="font-bold text-white font-[Outfit]">Moderation Action</span>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="p-5 overflow-y-auto scrollbar-thin space-y-4">
          {/* Report context */}
          <div className="bg-[#141322] rounded-xl p-3 text-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-slate-400">Reported user:</span>
              <span className="text-white font-semibold">{report.reported_alias || report.reported_user_id}</span>
              {report.priority === "high" && (
                <span className="flex items-center gap-1 text-[10px] font-mono bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full">
                  <AlertCircle className="w-2.5 h-2.5" /> HIGH PRIORITY
                </span>
              )}
            </div>
            <p className="text-slate-400">Reason: <span className="text-slate-200">{report.reason}</span></p>
            {report.message_content && (
              <p className="text-slate-500 italic mt-1 text-xs">"{report.message_content}"</p>
            )}
            {report.explanation && (
              <p className="text-slate-500 text-xs mt-1">Note: {report.explanation}</p>
            )}
          </div>

          {/* Action selection */}
          <div className="space-y-1.5">
            {ACTIONS.filter(a => !a.needsMsg || report.msg_id).map(a => (
              <label key={a.value}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                  actionType === a.value ? "border-purple-500/50 bg-purple-500/10" : "border-slate-700 hover:border-slate-600"
                }`}>
                <input type="radio" name="action" value={a.value}
                  checked={actionType === a.value} onChange={() => setActionType(a.value)}
                  className="accent-purple-500 shrink-0" />
                <span className={`${a.color} shrink-0`}>{a.icon}</span>
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">{a.label}</p>
                  <p className="text-xs text-slate-500">{a.desc}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Duration picker */}
          {selected?.hasDuration && (
            <div>
              <label className="block text-xs text-slate-400 mb-2 uppercase tracking-wide">Duration</label>
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map(d => (
                  <button key={d.value} onClick={() => setDurationHours(d.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      durationHours === d.value ? "border-purple-500/60 bg-purple-500/15 text-white" : "border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}>{d.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wide">Reason / Admin Notes</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Internal reason for this action (not shown to user)..."
              rows={2} maxLength={500}
              className="w-full bg-[#141322] border border-slate-700 rounded-xl py-2.5 px-3 text-white text-sm focus:outline-none focus:border-purple-500 resize-none" />
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 border border-slate-700 text-slate-300 rounded-xl text-sm hover:border-slate-500">Cancel</button>
            <button onClick={handleSubmit} disabled={!actionType || loading}
              className="flex-1 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-semibold rounded-xl text-sm disabled:opacity-40">
              {loading ? "Applying..." : "Apply Action"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── User history modal ─────────────────────────────────────────────────────────
function UserHistoryModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${userId}/history`).then(r => setData(r.data)).catch(() => {});
  }, [userId]);

  if (!data) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0E0E17] border border-purple-500/25 rounded-2xl w-full max-w-md shadow-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-purple-500/15 shrink-0">
          <div className="flex items-center gap-2.5">
            <AvatarDisplay avatarId={data.user?.avatar_id} size="sm" />
            <div>
              <span className="font-bold text-white">{data.user?.alias}</span>
              {data.active_mute && <span className="ml-2 text-[10px] font-mono bg-amber-500/20 text-amber-400 px-1.5 rounded-full">MUTED</span>}
              {data.user?.is_banned && <span className="ml-2 text-[10px] font-mono bg-red-500/20 text-red-400 px-1.5 rounded-full">BANNED</span>}
              {data.user?.is_suspended && <span className="ml-2 text-[10px] font-mono bg-orange-500/20 text-orange-400 px-1.5 rounded-full">SUSPENDED</span>}
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-5 overflow-y-auto scrollbar-thin space-y-5">
          <div>
            <p className="text-xs font-mono text-slate-500 uppercase tracking-wide mb-2">Reports Received ({data.reports_received.length})</p>
            {data.reports_received.length === 0
              ? <p className="text-xs text-slate-600">None</p>
              : data.reports_received.slice(0, 10).map(r => (
                <div key={r.report_id} className="flex items-center gap-2 py-1.5 border-b border-slate-800">
                  <span className="text-xs text-slate-400 flex-1">{r.reason}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_COLORS[r.status] || ""}`}>{r.status}</span>
                  <span className="text-[10px] text-slate-600">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              ))
            }
          </div>
          <div>
            <p className="text-xs font-mono text-slate-500 uppercase tracking-wide mb-2">Moderation Actions ({data.actions.length})</p>
            {data.actions.length === 0
              ? <p className="text-xs text-slate-600">No actions taken</p>
              : data.actions.map(a => (
                <div key={a.action_id} className="flex items-start gap-2 py-1.5 border-b border-slate-800">
                  <span className="text-xs text-white font-semibold w-24 shrink-0 capitalize">{a.action_type}</span>
                  <span className="text-xs text-slate-400 flex-1">{a.reason}</span>
                  <span className="text-[10px] text-slate-600 shrink-0">{new Date(a.created_at).toLocaleDateString()}</span>
                </div>
              ))
            }
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN ADMIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function AdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("tonight");
  const [stats, setStats] = useState({});
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [issues, setIssues] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [eveningStats, setEveningStats] = useState(null);
  const [photoInputs, setPhotoInputs] = useState({});  // roomId -> url string
  const [reportFilter, setReportFilter] = useState("pending");
  const [actionTarget, setActionTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [notification, setNotification] = useState("");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try { api.get("/admin/stats").then(r => setStats(r.data)); } catch {}
    try { api.get("/admin/rooms").then(r => setRooms(r.data)); } catch {}
    try { api.get("/admin/users").then(r => setUsers(r.data)); } catch {}
    try { api.get("/admin/issue-reports").then(r => setIssues(r.data)); } catch {}
    try { api.get("/admin/audit-log").then(r => setAuditLog(r.data)); } catch {}
    try { api.get("/admin/evening-stats").then(r => setEveningStats(r.data)); } catch {}
    fetchReports();
  };
  const fetchReports = async () => {
    try {
      const q = reportFilter && reportFilter !== "all" ? `?status=${reportFilter}` : "";
      const { data } = await api.get(`/admin/reports${q}`);
      setReports(data);
    } catch {}
  };
  useEffect(() => { fetchReports(); }, [reportFilter]);

  const notify = (msg) => { setNotification(msg); setTimeout(() => setNotification(""), 3000); };

  const roomAction = async (roomId, action) => {
    try {
      if (action === "approve") await api.post(`/admin/rooms/${roomId}/approve`);
      else if (action === "reject") await api.post(`/admin/rooms/${roomId}/reject`);
      else await api.delete(`/admin/rooms/${roomId}`);
      fetchAll(); notify("Done");
    } catch { notify("Failed"); }
  };

  const updateReportStatus = async (reportId, status) => {
    try {
      await api.post(`/admin/reports/${reportId}/status`, { status });
      fetchReports(); notify("Status updated");
    } catch { notify("Failed"); }
  };

  const updateIssue = async (issueId, status) => {
    try {
      await api.post(`/admin/issue-reports/${issueId}/update`, { status });
      fetchAll(); notify("Updated");
    } catch { notify("Failed"); }
  };

  const saveRoomPhoto = async (roomId) => {
    const url = (photoInputs[roomId] ?? "").trim();
    try {
      await api.post(`/admin/rooms/${roomId}/photo`, { photo_url: url });
      fetchAll();
      notify(url ? "Cover photo saved" : "Cover photo cleared");
    } catch { notify("Failed to update photo"); }
  };

  const removeMute = async (userId) => {
    try { await api.delete(`/admin/users/${userId}/mute`); fetchAll(); notify("Mute removed"); }
    catch { notify("Failed"); }
  };

  const TABS = [
    { id: "tonight",  label: "Tonight",  icon: <TrendingUp className="w-4 h-4" /> },
    { id: "reports",  label: "Reports",  icon: <Flag className="w-4 h-4" />,        badge: stats.pending_reports },
    { id: "rooms",    label: "Rooms",    icon: <MessageSquare className="w-4 h-4" />, badge: stats.pending_rooms },
    { id: "users",    label: "Users",    icon: <Users className="w-4 h-4" /> },
    { id: "issues",   label: "Issues",   icon: <HelpCircle className="w-4 h-4" />,   badge: stats.open_issues },
    { id: "audit",    label: "Audit Log",icon: <Shield className="w-4 h-4" /> },
    { id: "overview", label: "Overview", icon: <BarChart2 className="w-4 h-4" /> },
  ];

  const chartData = [
    { name: "Users",    value: stats.total_users || 0 },
    { name: "Messages", value: Math.min(stats.total_messages || 0, 9999) },
    { name: "Rooms",    value: stats.active_rooms || 0 },
    { name: "Reports",  value: stats.pending_reports || 0 },
  ];

  return (
    <div className="min-h-screen bg-[#07070B]">
      <header className="h-14 bg-[#0E0E17]/90 backdrop-blur-xl border-b border-purple-500/20 flex items-center px-4 gap-3 sticky top-0 z-40">
        <button data-testid="back-from-admin-btn" onClick={() => navigate("/lobby")} className="text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-white font-[Outfit]">Moderation Panel</h1>
        {stats.high_priority_reports > 0 && (
          <span className="flex items-center gap-1 ml-2 text-[11px] font-mono bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full border border-red-500/30">
            <AlertCircle className="w-3 h-3" /> {stats.high_priority_reports} HIGH PRIORITY
          </span>
        )}
        <span className="ml-auto text-xs font-mono text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded-full">ADMIN</span>
      </header>

      <div className="max-w-6xl mx-auto p-4">
        {notification && (
          <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl text-purple-300 text-sm text-center">{notification}</div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-[#0E0E17] border border-purple-500/15 rounded-xl p-1 mb-6 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                tab === t.id ? "bg-gradient-to-r from-violet-600 to-cyan-500 text-white" : "text-slate-400 hover:text-white"
              }`}>
              {t.icon} {t.label}
              {t.badge > 0 && (
                <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">{t.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tonight ── */}
        {tab === "tonight" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
            {eveningStats ? (
              <>
                {/* Status pill */}
                <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm w-fit ${
                  eveningStats.is_open
                    ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-300"
                    : "bg-[#141322] border-purple-500/20 text-slate-400"
                }`}>
                  <span className={`w-2 h-2 rounded-full ${eveningStats.is_open ? "bg-emerald-400 pulse-online" : "bg-slate-600"}`} />
                  {eveningStats.status_message}
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    ["Messages Tonight", eveningStats.total_messages,          "purple"],
                    ["Peak Concurrent",  eveningStats.peak_concurrent_users,   "cyan"],
                    ["Online Now",       eveningStats.currently_online,        "emerald"],
                    ["Session Date",     eveningStats.session_date,            "indigo"],
                    ["Founding Members", `${stats.founding_members ?? "—"} / ${stats.max_users || "∞"}`, "violet"],
                    ["Total Members",    stats.total_users ?? "—",             "slate"],
                  ].map(([label, value, color]) => (
                    <div key={label} className={`bg-[#0E0E17] border border-${color}-500/20 rounded-xl p-4`}>
                      <p className={`text-[10px] font-mono uppercase tracking-wide text-${color}-400 mb-1`}>{label}</p>
                      <p className="text-2xl font-black text-white font-[Outfit]">{value ?? "—"}</p>
                    </div>
                  ))}
                </div>

                {/* Per-room bar chart */}
                {eveningStats.room_stats.length > 0 ? (
                  <div className="bg-[#0E0E17] border border-purple-500/15 rounded-xl p-5">
                    <h3 className="font-semibold text-white mb-4 font-[Outfit]">Messages per Room</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={eveningStats.room_stats}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(139,92,246,0.1)" />
                        <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: "#141322", border: "1px solid rgba(139,92,246,0.3)", borderRadius: "8px", color: "#F8FAFC" }} />
                        <Bar dataKey="messages" fill="url(#nightGrad)" radius={[4, 4, 0, 0]} />
                        <defs>
                          <linearGradient id="nightGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#8B5CF6" />
                            <stop offset="100%" stopColor="#06B6D4" />
                          </linearGradient>
                        </defs>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-600">
                    <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No messages yet tonight.</p>
                    <p className="text-sm mt-1">Stats will appear here once the session is active.</p>
                  </div>
                )}

                <button onClick={() => { api.get("/admin/evening-stats").then(r => setEveningStats(r.data)); }}
                  className="text-xs text-purple-400 hover:text-purple-300 transition-colors">
                  ↻ Refresh
                </button>
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">Loading tonight's stats…</div>
            )}
          </motion.div>
        )}

        {/* ── Reports ── */}
        {tab === "reports" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {/* Filter bar */}
            <div className="flex gap-2 flex-wrap">
              {["pending", "under_review", "action_taken", "dismissed", "all"].map(s => (
                <button key={s} onClick={() => setReportFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize ${
                    reportFilter === s ? "bg-purple-500/20 border-purple-500/50 text-white" : "border-slate-700 text-slate-400 hover:border-slate-600"
                  }`}>{s.replace("_", " ")}</button>
              ))}
            </div>
            {reports.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><Flag className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No reports in this category</p></div>
            ) : reports.map(r => (
              <div key={r.report_id} data-testid={`admin-report-${r.report_id}`}
                className={`bg-[#0E0E17] border rounded-xl p-4 ${
                  r.priority === "high"
                    ? r.source === "auto_flagged" ? "border-amber-500/40" : "border-red-500/30"
                    : "border-purple-500/15"
                }`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {/* Auto-flagged source badge */}
                      {r.source === "auto_flagged" && (
                        <span className="flex items-center gap-1 text-[10px] font-mono bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full border border-amber-500/30">
                          🤖 AUTO-FLAGGED
                        </span>
                      )}
                      {r.priority === "high" && (
                        <span className="flex items-center gap-1 text-[10px] font-mono bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full border border-red-500/30">
                          <AlertCircle className="w-2.5 h-2.5" /> HIGH PRIORITY
                        </span>
                      )}
                      {r.auto_deleted && (
                        <span className="text-[10px] font-mono bg-slate-700/60 text-slate-400 px-1.5 py-0.5 rounded-full border border-slate-600">
                          ✓ AUTO-DELETED
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border capitalize font-mono ${STATUS_COLORS[r.status] || ""}`}>
                        {r.status?.replace("_"," ")}
                      </span>
                      <span className="text-[10px] bg-slate-700/50 text-slate-400 px-1.5 rounded-full capitalize">{r.report_type}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-white">
                        {r.reported_alias || r.reported_user_id}
                      </span>
                      <span className="text-xs text-slate-400">— {r.reason?.replace("_"," ")}</span>
                      {r.confidence != null && (
                        <span className="text-[10px] font-mono text-slate-500">
                          {(r.confidence * 100).toFixed(0)}% confidence
                        </span>
                      )}
                    </div>
                    {r.explanation && (
                      <p className="text-xs text-slate-500 mb-0.5">{r.explanation}</p>
                    )}
                    {r.message_content && (
                      <p className="text-xs text-slate-500 italic mb-1">"{r.message_content}"</p>
                    )}
                    <p className="text-[10px] text-slate-600 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    {r.status === "pending" && (
                      <button onClick={() => updateReportStatus(r.report_id, "under_review")}
                        className="px-2.5 py-1 text-[11px] border border-blue-500/30 text-blue-400 rounded-lg hover:bg-blue-500/10 transition-colors">
                        Review
                      </button>
                    )}
                    {r.status !== "dismissed" && r.status !== "action_taken" && (
                      <button data-testid={`take-action-${r.report_id}`} onClick={() => setActionTarget(r)}
                        className="px-2.5 py-1 text-[11px] bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-lg font-medium hover:opacity-90">
                        Action
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* ── Rooms ── */}
        {tab === "rooms" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {rooms.map(room => (
              <div key={room.room_id} data-testid={`admin-room-${room.room_id}`}
                className="bg-[#0E0E17] border border-purple-500/15 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-4">
                  {/* Cover photo thumbnail */}
                  {room.cover_photo && (
                    <img src={room.cover_photo} alt="" className="w-14 h-10 object-cover rounded-lg shrink-0 border border-purple-500/20" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-white">{room.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono border ${STATUS_COLORS[room.status] || "bg-slate-700 text-slate-400 border-slate-600"}`}>{room.status}</span>
                    </div>
                    <p className="text-xs text-slate-400 truncate">{room.description}</p>
                    <p className="text-[10px] text-slate-600">by {room.creator_alias}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {room.status === "pending" && (
                      <>
                        <button data-testid={`approve-room-${room.room_id}`} onClick={() => roomAction(room.room_id, "approve")}
                          className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs border border-emerald-500/30 hover:bg-emerald-500/30">
                          <Check className="w-3 h-3" />
                        </button>
                        <button data-testid={`reject-room-${room.room_id}`} onClick={() => roomAction(room.room_id, "reject")}
                          className="px-2.5 py-1 bg-red-500/10 text-red-400 rounded-lg text-xs border border-red-500/25 hover:bg-red-500/20">
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    )}
                    <button onClick={() => roomAction(room.room_id, "delete")}
                      className="px-2.5 py-1 border border-slate-700 text-slate-500 rounded-lg text-xs hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Cover photo URL input */}
                {room.status === "active" && (
                  <div className="flex items-center gap-2">
                    <Image className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                    <input
                      data-testid={`room-photo-input-${room.room_id}`}
                      value={photoInputs[room.room_id] ?? room.cover_photo ?? ""}
                      onChange={e => setPhotoInputs(p => ({ ...p, [room.room_id]: e.target.value }))}
                      placeholder="Cover photo URL (leave blank to use default)"
                      className="flex-1 bg-[#141322] border border-slate-700/60 rounded-lg py-1.5 px-3 text-white text-xs placeholder-slate-700 focus:outline-none focus:border-purple-500/50 transition-colors"
                    />
                    <button
                      data-testid={`room-photo-save-${room.room_id}`}
                      onClick={() => saveRoomPhoto(room.room_id)}
                      className="px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 text-purple-300 rounded-lg text-xs hover:bg-purple-500/30 transition-colors shrink-0"
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            ))}
          </motion.div>
        )}

        {/* ── Users ── */}
        {tab === "users" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
            {users.filter(u => u.role !== "admin").map(u => (
              <div key={u.user_id} data-testid={`admin-user-${u.user_id}`}
                className="flex items-center gap-3 bg-[#0E0E17] border border-purple-500/15 rounded-xl p-3">
                <AvatarDisplay avatarId={u.avatar_id} size="sm" status={u.is_online ? "online" : "offline"} showStatus />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-white text-sm">{u.alias || "(no alias)"}</span>
                    {u.is_banned && <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 rounded-full">Banned</span>}
                    {u.is_suspended && <span className="text-[10px] bg-orange-500/20 text-orange-400 px-1.5 rounded-full">Suspended</span>}
                    {u.active_mute && <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 rounded-full">Muted</span>}
                    {u.is_online && <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 rounded-full">Online</span>}
                  </div>
                  <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => setHistoryTarget(u.user_id)}
                    className="px-2.5 py-1 border border-slate-700 text-slate-400 rounded-lg text-xs hover:text-white hover:border-slate-500">
                    History
                  </button>
                  {u.active_mute && (
                    <button onClick={() => removeMute(u.user_id)}
                      className="px-2.5 py-1 border border-amber-500/30 text-amber-400 rounded-lg text-xs hover:bg-amber-500/10">
                      <Volume2 className="w-3 h-3" />
                    </button>
                  )}
                  <button
                    data-testid={`${u.is_banned ? "unban" : "ban"}-user-${u.user_id}`}
                    onClick={async () => {
                      if (u.is_banned) { await api.post(`/admin/users/${u.user_id}/unban`); }
                      else { await api.post(`/admin/users/${u.user_id}/ban`, { reason: "Admin action" }); }
                      fetchAll();
                    }}
                    className={`px-2.5 py-1 rounded-lg text-xs border flex items-center gap-1 transition-colors ${
                      u.is_banned ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/10 text-red-400 border-red-500/25 hover:bg-red-500/20"
                    }`}>
                    <Ban className="w-3 h-3" /> {u.is_banned ? "Unban" : "Ban"}
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* ── Issues ── */}
        {tab === "issues" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {issues.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><HelpCircle className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No issue reports</p></div>
            ) : issues.map(iss => (
              <div key={iss.issue_id} className="bg-[#0E0E17] border border-purple-500/15 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-white">{iss.category}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-mono ${STATUS_COLORS[iss.status] || ""}`}>{iss.status}</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">{iss.description}</p>
                    <p className="text-[10px] text-slate-600 mt-1">
                      {iss.user_alias} · {new Date(iss.created_at).toLocaleString()}
                    </p>
                  </div>
                  {iss.status === "open" && (
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => updateIssue(iss.issue_id, "in_progress")}
                        className="px-2.5 py-1 border border-blue-500/30 text-blue-400 rounded-lg text-xs hover:bg-blue-500/10">
                        Review
                      </button>
                      <button onClick={() => updateIssue(iss.issue_id, "resolved")}
                        className="px-2.5 py-1 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs hover:bg-emerald-500/10">
                        Resolve
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* ── Audit Log ── */}
        {tab === "audit" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
            {auditLog.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><Shield className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No audit entries yet</p></div>
            ) : auditLog.map(a => (
              <div key={a.audit_id} className="flex items-start gap-3 bg-[#0E0E17] border border-slate-800 rounded-xl p-3">
                <div className="w-2 h-2 rounded-full bg-purple-500 mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-white font-medium capitalize">{a.action.replace("_"," ")}</span>
                    <span className="text-xs text-slate-400">on <span className="text-slate-300">{a.target_user_alias}</span></span>
                    <span className="text-[10px] text-slate-500">by {a.admin_alias}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{a.reason}</p>
                  <p className="text-[10px] text-slate-700">{new Date(a.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* ── Overview ── */}
        {tab === "overview" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["Total Users",    stats.total_users || 0,           "purple"],
                ["Online Now",     stats.online_now || 0,            "emerald"],
                ["Active Rooms",   stats.active_rooms || 0,          "cyan"],
                ["Open Reports",   stats.pending_reports || 0,       "amber"],
                ["High Priority",  stats.high_priority_reports || 0, "red"],
                ["Open Issues",    stats.open_issues || 0,           "orange"],
                ["Pending Rooms",  stats.pending_rooms || 0,         "violet"],
                ["Total Messages", stats.total_messages || 0,        "indigo"],
              ].map(([label, value, color]) => (
                <div key={label} className={`bg-[#0E0E17] border border-${color}-500/20 rounded-xl p-4`}>
                  <p className={`text-xs font-mono uppercase tracking-wide text-${color}-400 mb-1`}>{label}</p>
                  <p className="text-2xl font-black text-white font-[Outfit]">{value}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#0E0E17] border border-purple-500/15 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-4 font-[Outfit]">Platform Overview</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(139,92,246,0.1)" />
                  <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#141322", border: "1px solid rgba(139,92,246,0.3)", borderRadius: "8px", color: "#F8FAFC" }} />
                  <Bar dataKey="value" fill="url(#barGrad)" radius={[4, 4, 0, 0]} />
                  <defs>
                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8B5CF6" />
                      <stop offset="100%" stopColor="#06B6D4" />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {actionTarget && (
          <ActionModal report={actionTarget} onClose={() => setActionTarget(null)}
            onDone={() => { setActionTarget(null); fetchAll(); notify("Action applied."); }} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {historyTarget && (
          <UserHistoryModal userId={historyTarget} onClose={() => setHistoryTarget(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
